<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import BPage from 'src/shared/components/base/BPage/BPage.vue';
import { useSchoolData } from 'src/modules/School/composables/useSchoolData';
import { useAuth } from 'src/boot/auth';
import { useSchoolFiltersStore } from 'src/stores/schoolFilters';

const schoolFiltersStore = useSchoolFiltersStore();
const { currentUser } = useAuth();

const selectedSchool = computed<string>({
  get() {
    return (
      schoolFiltersStore.selectedSchoolDocumentId ||
      currentUser.value?.school?.documentId ||
      ''
    );
  },
  set(value: string) {
    schoolFiltersStore.setSelectedSchoolDocumentId(value || '');
  },
});

const { loading, fetchClassesBySemester } = useSchoolData();

const selectedSemester = computed<string>({
  get() {
    return schoolFiltersStore.selectedSemesterDocumentId || '';
  },
  set(value: string) {
    schoolFiltersStore.setSelectedSemesterDocumentId(value || '');
  },
});
const classes = ref<Array<{ documentId: string; name: string; code: string }>>(
  [],
);
const selectedClassFilter = ref<string>(
  schoolFiltersStore.selectedClassDocumentId || '',
);

const classOptions = computed(() =>
  classes.value.map((schoolClass) => ({
    label: `${schoolClass.name} (${schoolClass.code})`,
    value: schoolClass.documentId,
  })),
);

const filteredClasses = computed(() => {
  if (!selectedClassFilter.value) return classes.value;
  return classes.value.filter(
    (schoolClass) => schoolClass.documentId === selectedClassFilter.value,
  );
});

watch(
  () => selectedClassFilter.value,
  (classDocumentId) => {
    schoolFiltersStore.setSelectedClassDocumentId(classDocumentId || '');
  },
);

watch(
  [() => selectedSemester.value, () => selectedSchool.value],
  async ([semesterDocumentId, schoolDocumentId]) => {
    if (!semesterDocumentId) {
      classes.value = [];
      return;
    }
    classes.value = await fetchClassesBySemester(
      semesterDocumentId,
      schoolDocumentId || undefined,
    );

    const hasSelectedClass = classes.value.some(
      (schoolClass) => schoolClass.documentId === selectedClassFilter.value,
    );
    if (!hasSelectedClass) {
      selectedClassFilter.value = '';
    }
  },
  { immediate: true },
);
</script>

<template>
  <BPage>
    <div class="q-pa-md column q-gutter-md">
      <q-select
        v-model="selectedClassFilter"
        :options="classOptions"
        emit-value
        map-options
        clearable
        outlined
        class="bg-white text-dark"
        popup-content-class="bg-white text-dark"
        :label="$t('CLASS')"
      />

      <q-inner-loading :showing="loading">
        <q-spinner-gears
          size="60px"
          color="primary"
        />
      </q-inner-loading>

      <q-banner
        v-if="!filteredClasses.length"
        :class="$q.dark.isActive ? 'bg-grey-9 text-white' : 'bg-grey-3 text-dark'"
      >
        {{ $t('SCHOOL_NO_CLASSES_FOR_SELECTED_SEMESTER') }}
      </q-banner>

      <q-list
        class="bg-white text-dark"
        bordered
        separator
      >
        <q-item
          v-for="schoolClass in filteredClasses"
          :key="schoolClass.documentId"
          clickable
          :to="{
            name: 'school.class.detail',
            params: { documentId: schoolClass.documentId },
          }"
        >
          <q-item-section>
            <q-item-label class="text-dark">{{
              schoolClass.name
            }}</q-item-label>
            <q-item-label
              caption
              class="text-grey-8"
              >{{ schoolClass.code }}</q-item-label
            >
          </q-item-section>
        </q-item>
      </q-list>
    </div>
  </BPage>
</template>
